// The deal's economics editor: dates, lease terms, fee components and payout, with a
// live preview of commission and payments computed by the tested engine as you type.
// Save stores the inputs, then syncDeal() regenerates payments and reminders.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { underContractDates, type Trigger } from '../lib/commission';
import { calculateDeal, type AbatementRow, type FeeRow, type LeaseTermsRow, type PayoutRow } from '../lib/dealCalc';
import { syncDeal } from '../lib/dealEngine';
import { date, money } from '../lib/format';
import { round2 } from '../lib/money';
import { supabase, unwrap, type Tables } from '../lib/supabase';
import { useToast } from './Toast';
import { Button, Card, ErrorBox, Field, Hint, Input, Select, Textarea } from './ui';

const BASIS_LABEL: Record<string, string> = {
  months_rent: "Months of rent",
  pct_lease_value: '% of aggregate lease value',
  pct_price: '% of price',
  flat: 'Flat fee ($)',
  per_sf: '$ per SF',
};
const PAYERS = ['landlord', 'seller', 'purchaser', 'tenant', 'other'];
const TRIGGER_LABEL: Record<string, string> = { lease_execution: 'Lease execution', commencement: 'Commencement', occupancy: 'Occupancy', closing: 'Closing', custom: 'Custom date' };

type S = string; // form fields are strings while editing
interface Draft {
  dates: Record<
    'lease_executed_date' | 'commencement_estimated_date' | 'commencement_confirmed_date' | 'occupancy_date' | 'contract_effective_date' | 'dd_end_date' | 'closing_date' | 'purchase_price',
    S
  > & { commencement_clause: S };
  lease: {
    rentable_sf: S;
    term_months: S;
    base_rent_mode: 'psf_year' | 'monthly';
    base_rent_value: S;
    escalation_kind: 'none' | 'percent' | 'fixed_psf' | 'custom';
    escalation_value: S;
    custom_schedule_mode: 'psf_year' | 'monthly';
    custom_schedule: S;
    opex_enabled: boolean;
    opex_psf_year: S;
  };
  abatements: { start_month: S; months: S; pct: S }[];
  fees: { id?: string; component_key: string; label: S; basis: string; rate: S; payer: string; split_pct: S; override_amount: S }[];
  payout: {
    kind: 'cash_out' | 'annuity' | 'at_closing';
    splits: { trigger: Trigger; pct: S; offsetDays: S; customDate: S }[];
    annuity_monthly_amount: S;
    annuity_months: S;
    annuity_trigger: Trigger;
    annuity_offset_days: S;
    annuity_custom_date: S;
  };
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const n = (s: string): number | null => (s.trim() === '' || isNaN(Number(s.replace(/,/g, ''))) ? null : Number(s.replace(/,/g, '')));
const d = (s: string): string | null => (s ? s : null);

export interface EconomicsProps {
  deal: Tables<'deals'>;
  isLease: boolean;
  terms: Tables<'deal_lease_terms'> | null;
  abatements: Tables<'deal_rent_abatements'>[];
  fees: Tables<'fee_components'>[];
  payout: Tables<'payout_structures'> | null;
}

function toDraft(p: EconomicsProps): Draft {
  const t = p.terms;
  const splits = (p.payout?.cash_splits as unknown as { trigger: Trigger; pct: number; offsetDays?: number | null; customDate?: string | null }[] | null) ?? [
    { trigger: 'lease_execution', pct: 50 },
    { trigger: 'commencement', pct: 50 },
  ];
  return {
    dates: {
      lease_executed_date: str(p.deal.lease_executed_date),
      commencement_estimated_date: str(p.deal.commencement_estimated_date),
      commencement_confirmed_date: str(p.deal.commencement_confirmed_date),
      commencement_clause: str(p.deal.commencement_clause),
      occupancy_date: str(p.deal.occupancy_date),
      contract_effective_date: str(p.deal.contract_effective_date),
      dd_end_date: str(p.deal.dd_end_date),
      closing_date: str(p.deal.closing_date),
      purchase_price: str(p.deal.purchase_price),
    },
    lease: {
      rentable_sf: str(t?.rentable_sf),
      term_months: str(t?.term_months),
      base_rent_mode: (t?.base_rent_mode as 'psf_year' | 'monthly') ?? 'psf_year',
      base_rent_value: str(t?.base_rent_value),
      // A lease with no rent entered yet starts at the usual 3% annual escalation (editable).
      escalation_kind: t?.base_rent_value ? (t.escalation_kind as Draft['lease']['escalation_kind']) : 'percent',
      escalation_value: t?.base_rent_value ? str(t.escalation_value) : '3',
      custom_schedule_mode: (t?.custom_schedule_mode as 'psf_year' | 'monthly') ?? 'monthly',
      custom_schedule: (t?.custom_schedule ?? []).join(', '),
      opex_enabled: !!t?.opex_enabled,
      opex_psf_year: str(t?.opex_psf_year),
    },
    abatements: p.abatements.map((a) => ({ start_month: str(a.start_month), months: str(a.months), pct: str(a.pct) })),
    fees: p.fees.map((f) => ({
      id: f.id,
      component_key: f.component_key,
      label: str(f.label),
      basis: f.basis,
      rate: str(f.rate),
      payer: f.payer,
      split_pct: str(f.split_pct),
      override_amount: str(f.override_amount),
    })),
    payout: {
      kind: (p.payout?.kind as Draft['payout']['kind']) ?? (p.isLease ? 'cash_out' : 'at_closing'),
      splits: splits.map((s) => ({ trigger: s.trigger, pct: str(s.pct), offsetDays: str(s.offsetDays), customDate: str(s.customDate) })),
      annuity_monthly_amount: str(p.payout?.annuity_monthly_amount),
      annuity_months: str(p.payout?.annuity_months),
      annuity_trigger: (p.payout?.annuity_trigger as Trigger) ?? 'commencement',
      annuity_offset_days: str(p.payout?.annuity_offset_days),
      annuity_custom_date: str(p.payout?.annuity_custom_date),
    },
  };
}

function draftRows(dr: Draft) {
  const leaseTerms: Partial<LeaseTermsRow> = {
    rentable_sf: n(dr.lease.rentable_sf),
    term_months: n(dr.lease.term_months),
    base_rent_mode: dr.lease.base_rent_mode,
    base_rent_value: n(dr.lease.base_rent_value),
    escalation_kind: dr.lease.escalation_kind,
    escalation_value: n(dr.lease.escalation_value),
    custom_schedule_mode: dr.lease.custom_schedule_mode,
    custom_schedule: dr.lease.custom_schedule.split(/[,\s]+/).map((x) => n(x)).filter((x): x is number => x !== null),
    opex_enabled: dr.lease.opex_enabled,
    opex_psf_year: n(dr.lease.opex_psf_year),
  };
  const abatements: AbatementRow[] = dr.abatements
    .map((a) => ({ start_month: n(a.start_month) ?? 0, months: n(a.months) ?? 0, pct: n(a.pct) ?? 0 }))
    .filter((a) => a.start_month > 0 && a.months > 0 && a.pct > 0);
  const fees: FeeRow[] = dr.fees.map((f) => ({
    component_key: f.component_key,
    label: f.label || null,
    basis: f.basis,
    rate: n(f.rate),
    payer: f.payer,
    split_pct: n(f.split_pct),
    override_amount: n(f.override_amount),
  }));
  const payout: PayoutRow = {
    kind: dr.payout.kind,
    cash_splits:
      dr.payout.kind === 'cash_out'
        ? (dr.payout.splits.map((s) => ({ trigger: s.trigger, pct: n(s.pct) ?? 0, offsetDays: n(s.offsetDays), customDate: d(s.customDate) })) as never)
        : null,
    annuity_monthly_amount: dr.payout.kind === 'annuity' ? n(dr.payout.annuity_monthly_amount) : null,
    annuity_months: dr.payout.kind === 'annuity' ? n(dr.payout.annuity_months) : null,
    annuity_trigger: dr.payout.kind === 'annuity' ? dr.payout.annuity_trigger : null,
    annuity_offset_days: dr.payout.kind === 'annuity' ? n(dr.payout.annuity_offset_days) : null,
    annuity_custom_date: dr.payout.kind === 'annuity' ? d(dr.payout.annuity_custom_date) : null,
    closing_offset_days: null,
  };
  const dates = {
    leaseExecuted: d(dr.dates.lease_executed_date),
    commencementEstimated: d(dr.dates.commencement_estimated_date),
    commencementConfirmed: d(dr.dates.commencement_confirmed_date),
    occupancy: d(dr.dates.occupancy_date),
    contractEffective: d(dr.dates.contract_effective_date),
    ddEnd: d(dr.dates.dd_end_date),
    closing: d(dr.dates.closing_date),
  };
  return { leaseTerms, abatements, fees, payout, dates, purchasePrice: n(dr.dates.purchase_price) };
}

export function DealEconomics(props: EconomicsProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { deal, isLease } = props;
  const [draft, setDraft] = useState<Draft>(() => toDraft(props));
  const [dirty, setDirty] = useState(false);
  const rows = useMemo(() => draftRows(draft), [draft]);
  const calc = useMemo(() => calculateDeal({ isLease, ...rows }), [isLease, rows]);
  const uc = underContractDates(rows.dates);

  function update(fn: (d: Draft) => void) {
    setDraft((prev) => {
      const next = structuredClone(prev);
      fn(next);
      return next;
    });
    setDirty(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const id = deal.id;
      unwrap(
        await supabase
          .from('deals')
          .update({
            lease_executed_date: d(draft.dates.lease_executed_date),
            commencement_estimated_date: d(draft.dates.commencement_estimated_date),
            commencement_confirmed_date: d(draft.dates.commencement_confirmed_date),
            commencement_clause: draft.dates.commencement_clause || null,
            occupancy_date: d(draft.dates.occupancy_date),
            contract_effective_date: d(draft.dates.contract_effective_date),
            // Only what Brett typed. Blank DD/closing dates keep following the defaults
            // (+45 / +30 days), so they move when the contract date changes.
            dd_end_date: d(draft.dates.dd_end_date),
            closing_date: d(draft.dates.closing_date),
            purchase_price: rows.purchasePrice,
          })
          .eq('id', id),
      );
      if (isLease) {
        const t = rows.leaseTerms;
        unwrap(
          await supabase.from('deal_lease_terms').upsert(
            {
              deal_id: id,
              rentable_sf: t.rentable_sf,
              term_months: t.term_months,
              base_rent_mode: t.base_rent_value ? t.base_rent_mode : null,
              base_rent_value: t.base_rent_value,
              escalation_kind: t.escalation_kind ?? 'none',
              escalation_value: t.escalation_value,
              custom_schedule_mode: t.escalation_kind === 'custom' ? t.custom_schedule_mode : null,
              custom_schedule: t.escalation_kind === 'custom' ? t.custom_schedule : null,
              opex_enabled: t.opex_enabled ?? false,
              opex_psf_year: t.opex_enabled ? t.opex_psf_year : null,
            },
            { onConflict: 'deal_id' },
          ),
        );
        unwrap(await supabase.from('deal_rent_abatements').delete().eq('deal_id', id));
        if (rows.abatements.length) unwrap(await supabase.from('deal_rent_abatements').insert(rows.abatements.map((a) => ({ ...a, deal_id: id }))));
      }
      // Fee components: remove deleted ones, upsert the rest in order.
      const keep = draft.fees.map((f) => f.id).filter(Boolean) as string[];
      const removed = props.fees.filter((f) => !keep.includes(f.id)).map((f) => f.id);
      if (removed.length) unwrap(await supabase.from('fee_components').delete().in('id', removed));
      for (const [i, f] of draft.fees.entries()) {
        const row = {
          deal_id: id,
          component_key: f.component_key,
          label: f.label || null,
          sort_order: i,
          basis: f.basis,
          rate: n(f.rate),
          payer: f.payer,
          split_pct: n(f.split_pct),
          override_amount: n(f.override_amount),
        };
        if (f.id) unwrap(await supabase.from('fee_components').update(row).eq('id', f.id));
        else unwrap(await supabase.from('fee_components').insert(row));
      }
      unwrap(await supabase.from('payout_structures').upsert({ deal_id: id, ...rows.payout }, { onConflict: 'deal_id' }));
      return syncDeal(id);
    },
    onSuccess: (r) => {
      setDirty(false);
      // Shown as a bubble: this form reloads with the saved data, which would wipe an inline note.
      const p = r.payments;
      toast({
        message: `Saved. Payments: ${p.inserted} added, ${p.updated} updated, ${p.deleted} removed${p.locked ? `, ${p.locked} invoiced/received left untouched` : ''}.`,
        autoHideMs: 6000,
      });
      qc.invalidateQueries({ queryKey: ['deal', deal.id] });
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['nav-counts'] });
    },
  });

  const L = draft.lease;
  const dateInput = (k: keyof Draft['dates'], label: string, hint?: string) => (
    <Field label={label} hint={hint}>
      <Input type="date" value={draft.dates[k]} onChange={(e) => update((x) => void (x.dates[k] = e.target.value))} />
    </Field>
  );

  return (
    <div className="space-y-4">
      <Card title="Key dates">
        {isLease ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {dateInput('lease_executed_date', 'Lease executed', 'Drives the first payment')}
            {dateInput('commencement_estimated_date', 'Commencement (estimated)')}
            {dateInput('commencement_confirmed_date', 'Commencement (confirmed)', 'From the commencement letter')}
            {dateInput('occupancy_date', 'Occupancy', 'Blank = use commencement')}
            <Field label="Commencement clause" className="col-span-2 sm:col-span-4" hint="Paste the lease language, e.g. “earlier of 90 days after mutual execution or issuance of a CO”">
              <Textarea rows={2} value={draft.dates.commencement_clause} onChange={(e) => update((x) => void (x.dates.commencement_clause = e.target.value))} />
            </Field>
            <div className="col-span-2 text-sm text-slate-600 sm:col-span-4">
              Lease expiration:{' '}
              {calc.expiration.date ? (
                <b>
                  {date(calc.expiration.date)} ({calc.expiration.basis})
                </b>
              ) : (
                <span className="text-slate-400">needs a commencement date and term</span>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Purchase / sale price">
              <Input inputMode="decimal" value={draft.dates.purchase_price} onChange={(e) => update((x) => void (x.dates.purchase_price = e.target.value))} placeholder="4,000,000" />
            </Field>
            {dateInput('contract_effective_date', 'Contract effective')}
            {dateInput('dd_end_date', 'Due diligence ends', uc.ddEnd && !draft.dates.dd_end_date ? `Default: ${date(uc.ddEnd)} (+45 days)` : undefined)}
            {dateInput('closing_date', 'Closing', uc.closing && !draft.dates.closing_date ? `Default: ${date(uc.closing)} (+30 after DD)` : undefined)}
          </div>
        )}
      </Card>

      {isLease && (
        <Card title="Lease economics">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Rentable SF">
              <Input inputMode="decimal" value={L.rentable_sf} onChange={(e) => update((x) => void (x.lease.rentable_sf = e.target.value))} />
            </Field>
            <Field label="Term (months)">
              <Input inputMode="numeric" value={L.term_months} onChange={(e) => update((x) => void (x.lease.term_months = e.target.value))} />
            </Field>
            <Field label="Starting rent entered as">
              <Select value={L.base_rent_mode} onChange={(e) => update((x) => void (x.lease.base_rent_mode = e.target.value as 'psf_year' | 'monthly'))}>
                <option value="psf_year">$/SF/yr</option>
                <option value="monthly">$ per month</option>
              </Select>
            </Field>
            <Field
              label={L.base_rent_mode === 'psf_year' ? 'Starting rent ($/SF/yr)' : 'Starting rent ($/month)'}
              hint={
                calc.lease
                  ? L.base_rent_mode === 'psf_year'
                    ? `= ${money(calc.lease.startingMonthlyRent)}/month`
                    : calc.lease.startingRentPsfYear
                      ? `= $${calc.lease.startingRentPsfYear.toFixed(2)}/SF/yr`
                      : undefined
                  : undefined
              }
            >
              <Input inputMode="decimal" value={L.base_rent_value} onChange={(e) => update((x) => void (x.lease.base_rent_value = e.target.value))} />
            </Field>
            <Field label="Escalation">
              <Select value={L.escalation_kind} onChange={(e) => update((x) => void (x.lease.escalation_kind = e.target.value as Draft['lease']['escalation_kind']))}>
                <option value="percent">Annual %</option>
                <option value="fixed_psf">Fixed $/SF bump</option>
                <option value="custom">Year-by-year schedule</option>
                <option value="none">None</option>
              </Select>
            </Field>
            {(L.escalation_kind === 'percent' || L.escalation_kind === 'fixed_psf') && (
              <Field label={L.escalation_kind === 'percent' ? 'Escalation %' : '$/SF per year'}>
                <Input inputMode="decimal" value={L.escalation_value} onChange={(e) => update((x) => void (x.lease.escalation_value = e.target.value))} />
              </Field>
            )}
            {L.escalation_kind === 'custom' && (
              <>
                <Field label="Schedule in">
                  <Select value={L.custom_schedule_mode} onChange={(e) => update((x) => void (x.lease.custom_schedule_mode = e.target.value as 'psf_year' | 'monthly'))}>
                    <option value="monthly">$ per month</option>
                    <option value="psf_year">$/SF/yr</option>
                  </Select>
                </Field>
                <Field label="Rent by year" hint="Year 1, year 2, … separated by commas" className="col-span-2">
                  <Input value={L.custom_schedule} onChange={(e) => update((x) => void (x.lease.custom_schedule = e.target.value))} placeholder="5000, 5150, 5305" />
                </Field>
              </>
            )}
            <div className="col-span-2 flex items-end gap-3 sm:col-span-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={L.opex_enabled} onChange={(e) => update((x) => void (x.lease.opex_enabled = e.target.checked))} />
                Paid on OPEX
              </label>
              {L.opex_enabled && (
                <Field label="Estimated OPEX $/SF/yr (flat, never trued up)">
                  <Input inputMode="decimal" value={L.opex_psf_year} onChange={(e) => update((x) => void (x.lease.opex_psf_year = e.target.value))} className="!w-32" />
                </Field>
              )}
            </div>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-medium text-slate-600">Free rent (abatement)</div>
              <Button size="sm" variant="ghost" onClick={() => update((x) => void x.abatements.push({ start_month: '1', months: '', pct: '100' }))}>
                <Plus className="h-3.5 w-3.5" /> Period
              </Button>
            </div>
            {draft.abatements.map((a, i) => (
              <div key={i} className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                Starting month
                <Input className="!w-16" value={a.start_month} onChange={(e) => update((x) => void (x.abatements[i]!.start_month = e.target.value))} />
                for
                <Input className="!w-16" value={a.months} onChange={(e) => update((x) => void (x.abatements[i]!.months = e.target.value))} />
                months at
                <Input className="!w-16" value={a.pct} onChange={(e) => update((x) => void (x.abatements[i]!.pct = e.target.value))} />% off
                <button className="text-slate-400 hover:text-rose-600" onClick={() => update((x) => void x.abatements.splice(i, 1))} aria-label="Remove period">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          {calc.lease && (
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-slate-50 p-3 text-sm sm:grid-cols-3">
              <Stat label="Aggregate base rent" value={money(round2(calc.lease.aggregateBaseRent))} />
              {calc.lease.abatedRent > 0 && <Stat label="Free rent excluded" value={money(round2(calc.lease.abatedRent))} />}
              {calc.lease.opexEnabled && <Stat label="Aggregate OPEX" value={money(round2(calc.lease.aggregateOpex))} />}
              <Stat label="Aggregate lease value" value={money(round2(calc.lease.aggregateLeaseValue))} strong />
              <Stat
                label={`Monthly rent for commission${calc.lease.firstFullMonth && calc.lease.firstFullMonth > 1 ? ` (month ${calc.lease.firstFullMonth})` : ''}`}
                value={calc.lease.commissionMonthlyRent === null ? '—' : money(round2(calc.lease.commissionMonthlyRent))}
              />
            </dl>
          )}
          <Hint>{calc.leaseError}</Hint>
        </Card>
      )}

      <Card
        title="Commission"
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              update((x) => void x.fees.push({ component_key: `fee_${Date.now()}`, label: '', basis: isLease ? 'flat' : 'pct_price', rate: '', payer: isLease ? 'landlord' : 'seller', split_pct: '', override_amount: '' }))
            }
          >
            <Plus className="h-3.5 w-3.5" /> Component
          </Button>
        }
      >
        <div className="space-y-2">
          {draft.fees.map((f, i) => {
            const res = calc.fees?.components[i];
            return (
              <div key={f.id ?? f.component_key} className="rounded-md border border-slate-200 p-2.5">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
                  <Field label="Label" className="col-span-2 sm:col-span-3">
                    <Input value={f.label} onChange={(e) => update((x) => void (x.fees[i]!.label = e.target.value))} />
                  </Field>
                  <Field label="Basis" className="col-span-2 sm:col-span-3">
                    <Select value={f.basis} onChange={(e) => update((x) => void (x.fees[i]!.basis = e.target.value))}>
                      {Object.entries(BASIS_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={f.basis === 'months_rent' ? 'Months' : f.basis.startsWith('pct') ? 'Rate %' : 'Amount'} className="sm:col-span-2">
                    <Input inputMode="decimal" value={f.rate} onChange={(e) => update((x) => void (x.fees[i]!.rate = e.target.value))} />
                  </Field>
                  <Field label="Paid by" className="sm:col-span-2">
                    <Select value={f.payer} onChange={(e) => update((x) => void (x.fees[i]!.payer = e.target.value))}>
                      {PAYERS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Your split %" className="sm:col-span-1">
                    <Input inputMode="decimal" placeholder="100" title="Your share of this fee after the house / co-broker. Blank = 100%." value={f.split_pct} onChange={(e) => update((x) => void (x.fees[i]!.split_pct = e.target.value))} />
                  </Field>
                  <Field label="Override $" className="sm:col-span-1">
                    <Input inputMode="decimal" value={f.override_amount} onChange={(e) => update((x) => void (x.fees[i]!.override_amount = e.target.value))} />
                  </Field>
                </div>
                <div className="mt-1.5 flex items-center gap-3 text-sm">
                  {res ? (
                    <>
                      <span className="font-semibold text-slate-900">{money(res.amount)}</span>
                      {res.overridden && (
                        <span className="rounded bg-amber-100 px-1.5 text-xs font-medium text-amber-800" title={`Calculated: ${money(res.calculated)}`}>
                          overridden (calc. {money(res.calculated)})
                        </span>
                      )}
                      {res.splitAmount > 0 && <span className="text-xs text-slate-500">− {money(res.splitAmount)} house / co-broker = {money(res.net)} net to you</span>}
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">—</span>
                  )}
                  <button className="ml-auto text-slate-400 hover:text-rose-600" onClick={() => update((x) => void x.fees.splice(i, 1))} aria-label="Remove component">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {calc.fees && (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 pt-2 text-sm">
            <span>
              Total commission: <b>{money(calc.fees.gross)}</b>
            </span>
            {calc.fees.split > 0 && (
              <span>
                Net to you: <b>{money(calc.fees.net)}</b>
              </span>
            )}
          </div>
        )}
        {isLease && !calc.lease && !calc.leaseError ? (
          <Hint tone="gray">Enter the term and starting rent above (plus rentable SF if rent is per SF) to calculate the commission.</Hint>
        ) : (
          <Hint>{calc.feeError}</Hint>
        )}
      </Card>

      <Card title="Payout">
        <div className="space-y-3">
          <Select value={draft.payout.kind} onChange={(e) => update((x) => void (x.payout.kind = e.target.value as Draft['payout']['kind']))} className="!w-56">
            {isLease && <option value="cash_out">Cash out</option>}
            {isLease && <option value="annuity">Annuity</option>}
            <option value="at_closing">100% at closing</option>
          </Select>
          {draft.payout.kind === 'cash_out' && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => update((x) => void (x.payout.splits = [{ trigger: 'lease_execution', pct: '50', offsetDays: '', customDate: '' }, { trigger: 'commencement', pct: '50', offsetDays: '', customDate: '' }]))}>
                  50/50 execution / commencement
                </Button>
                <Button size="sm" onClick={() => update((x) => void (x.payout.splits = [{ trigger: 'lease_execution', pct: '100', offsetDays: '', customDate: '' }]))}>
                  100% at execution
                </Button>
              </div>
              {draft.payout.splits.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
                  <Input className="!w-16" value={s.pct} onChange={(e) => update((x) => void (x.payout.splits[i]!.pct = e.target.value))} />% at
                  <Select className="!w-40" value={s.trigger} onChange={(e) => update((x) => void (x.payout.splits[i]!.trigger = e.target.value as Trigger))}>
                    {['lease_execution', 'commencement', 'occupancy', 'custom'].map((t) => (
                      <option key={t} value={t}>
                        {TRIGGER_LABEL[t]}
                      </option>
                    ))}
                  </Select>
                  {s.trigger === 'custom' ? (
                    <Input type="date" className="!w-40" value={s.customDate} onChange={(e) => update((x) => void (x.payout.splits[i]!.customDate = e.target.value))} />
                  ) : (
                    <>
                      +
                      <Input className="!w-16" value={s.offsetDays} placeholder={s.trigger === 'occupancy' ? '30' : '0'} onChange={(e) => update((x) => void (x.payout.splits[i]!.offsetDays = e.target.value))} />
                      days
                    </>
                  )}
                  <button className="text-slate-400 hover:text-rose-600" onClick={() => update((x) => void x.payout.splits.splice(i, 1))} aria-label="Remove">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={() => update((x) => void x.payout.splits.push({ trigger: 'commencement', pct: '', offsetDays: '', customDate: '' }))}>
                <Plus className="h-3.5 w-3.5" /> Payment
              </Button>
            </div>
          )}
          {draft.payout.kind === 'annuity' && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              $<Input className="!w-28" value={draft.payout.annuity_monthly_amount} onChange={(e) => update((x) => void (x.payout.annuity_monthly_amount = e.target.value))} />
              per month ×
              <Input className="!w-16" value={draft.payout.annuity_months} onChange={(e) => update((x) => void (x.payout.annuity_months = e.target.value))} />
              months, starting at
              <Select className="!w-40" value={draft.payout.annuity_trigger} onChange={(e) => update((x) => void (x.payout.annuity_trigger = e.target.value as Trigger))}>
                {['lease_execution', 'commencement', 'occupancy', 'custom'].map((t) => (
                  <option key={t} value={t}>
                    {TRIGGER_LABEL[t]}
                  </option>
                ))}
              </Select>
              {draft.payout.annuity_trigger === 'custom' ? (
                <Input type="date" className="!w-40" value={draft.payout.annuity_custom_date} onChange={(e) => update((x) => void (x.payout.annuity_custom_date = e.target.value))} />
              ) : (
                <>
                  +
                  <Input className="!w-16" value={draft.payout.annuity_offset_days} placeholder="0" onChange={(e) => update((x) => void (x.payout.annuity_offset_days = e.target.value))} />
                  days
                </>
              )}
            </div>
          )}
          {calc.plan && (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {calc.plan.payments.map((p) => (
                  <tr key={p.slotKey}>
                    <td className="py-1 text-slate-600">{p.label}</td>
                    <td className="py-1 text-slate-500">{p.expectedDate ? date(p.expectedDate) : 'date not known yet'}</td>
                    <td className="py-1 text-right font-medium">{money(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {calc.plan?.annuityDifference !== null && calc.plan?.annuityDifference !== undefined && (
            <div className="text-sm text-slate-600">
              Annuity total {money(calc.plan.total)} vs. calculated commission {money(calc.fees?.net)}:{' '}
              <b className={calc.plan.annuityDifference < 0 ? 'text-rose-600' : 'text-emerald-700'}>
                {calc.plan.annuityDifference >= 0 ? '+' : ''}
                {money(calc.plan.annuityDifference)}
              </b>
            </div>
          )}
          <Hint>{calc.planError}</Hint>
        </div>
      </Card>

      {/* Buttons on the left: the live site's "Powered by Netlify" badge covers the bottom-right corner. */}
      <div className="sticky bottom-3 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white/95 px-4 py-2.5 shadow-sm">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!dirty}>
          Save & recalculate
        </Button>
        <Button
          onClick={() => {
            setDraft(toDraft(props));
            setDirty(false);
          }}
          disabled={!dirty}
        >
          Discard
        </Button>
        {dirty && (
          <span className="flex items-center gap-1 text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4" /> Unsaved changes
          </span>
        )}
        <ErrorBox error={save.error} />
      </div>
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={strong ? 'font-semibold text-slate-900' : 'text-slate-800'}>{value}</dd>
    </div>
  );
}
